import React, { useState } from "react";
import moment from "moment";
import { Grid } from "@mui/material";

import { timesheetApi, workScheduleApi } from "@api";
import { PLACEHOLDER_CONTRACTOR } from "@configs";
import { AppCustomeSelect, CircularLoader } from "..";
import { selectAuth, useAppSelector } from "@redux";
import { TableWrapView } from "./TableWrapView";
import "../../App.css";
import { useUserSelectionHistories } from "src/Utils/Hooks/useUserSelectionHistories";
import { useGroupSelectionHistories } from "src/Utils/Hooks/useGroupSelectionHistories";
import { useAppNavigation } from "@utils";

const defaultGroup = {
  id: 0,
  name: "全て",
};

type Contractor = {
  color: string | null;
  created_at: string; // "2022-07-12"
  id: number; //2
  name: string | null; // "株式会社WIND"
  note: string | null; // "今井 清彦\n\n事務所→〒406-0034山梨県笛吹市石和町唐柏958 桐林住宅1号室"
}

type Group = {
  company_id: number; // 1
  created_at: string; // "2022-07-28"
  deleted_at: string | null;
  id: number; // 3
  name: string; // "小俣班"
  note: string | null;
  skip_leader: number; // 0
  status: number; // 1
  updated_at: string; // "2024-08-04"
}

// This could be a contractor, person, or group. Used to populate the 班・従業員 dropdown
type DropdownOption = {
  label: string; // "山田 太郎"
  value: number; // 3
  type: "employee" | "group" | "contractor" | null;
}

type GroupUser = {
  user_id: number; // 3
  group_id: number; // 2
}

type Employee = {
  id: number; // 3
  first_name: string; // "太郎"
  last_name: string; // "山田"
  avatar: string | null;
}

type WorkSchedule = {
  employee: Employee;
  end_date: string; // "2061-12-15"
  id: number; // 5
  name: string; // "無"
  start_date: string; // "2021-09-10"
  work_schedules: any[]; // [] I don't know what this is supposed to hold, it always seems to be an empty array
}

export const WorkScheduleWrap = () => {
  const auth = useAppSelector(selectAuth);
  const id = auth?.userInfo?.id;  
  const userSelected = React.useRef(false);
  const { navigateWithState, getQueryParam, currentPath, currentQueryString } = useAppNavigation();

  // クエリの「値がある」を厳密判定（null/undefined/空文字は未指定扱い）
  const hasQueryValue = (v: any) => v !== null && v !== undefined && v !== "";

  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [workSchedules, setWorkSchedules] = useState<WorkSchedule[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<DropdownOption[]>([]);
  const [employeeOptionsRaw, setEmployeeOptionsRaw] = useState<any[]>([]);
  const [selectSubContractor, setSelectSubContractor] = useState<any>("");
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [peopleInput, setPeopleInput] = useState<DropdownOption | null>(null);

  const { list, fetchList, storeSelection } = useUserSelectionHistories(id ?? 0);
  const groupsUsers = useAppSelector((state) => state.preload.groupsUsers);
  const { groupList, fetchGroupList, storeGroupSelection } = useGroupSelectionHistories(id ?? 0);

  const contractorOptions = (!contractors || contractors.length === 0) ? [] :
    contractors.map((contractor) => {
      return {
        label: `${contractor.name}`,
        value: contractor?.id?.toString(),
        type: "contractor",
      };
    });

  const groupOptions: DropdownOption[] = (!groupList || groupList.length === 0) ? [] :
    groupList.map((group) => {
      return {
        label: group.name,
        value: group.id,
        type: "group",
      };
    });

  const peopleOptions: DropdownOption[] = React.useMemo(() => {
    const merged = [...employeeOptions, ...groupOptions];
    return merged;
  }, [employeeOptions, groupOptions]);

  // console.log("peopleInput", peopleInput);
  // console.log("workSchedules", workSchedules);
  // console.log("peopleInput", peopleInput);
  // console.log("groupList", groupList);
  // console.log("groupsUsers", groupsUsers);

  const filterByGroup = () => {
    if (!peopleInput || peopleInput.type !== "group") return workSchedules;

    return workSchedules.filter(ws =>
      groupsUsers.some(
        gu => gu.group_id === peopleInput.value && gu.user_id === ws.employee.id
      )
    );
  };

  const filterByUser = () => {
    if (!peopleInput || peopleInput.type !== "employee") return workSchedules;

    return workSchedules.filter(ws => ws.employee.id === peopleInput.value);
  };

  const filterByPeople = () => {
    if (!peopleInput) return workSchedules;

    if (peopleInput.type === "employee") {
      return filterByUser();
    }
    if (peopleInput.type === "group") {
      return filterByGroup();
    }
    return workSchedules;
  };

  React.useEffect(() => {
    fetchListWorkSchedules();
  }, [date]);

  React.useEffect(() => {
    fetchContractorsList();
  }, []);

  const fetchContractorsList = async () => {
    try {
      const responseContractorsList = await timesheetApi.getListContractors();
      setContractors(responseContractorsList?.data?.data?.contractors);
    } catch (error) {
    } finally {
      //
    }
  };

  const fetchListWorkSchedules = async () => {
    setLoading(true);

    try {
      const response = await workScheduleApi.getWorkSchedule({ month: date && moment(date).format("YYYY-MM") });

      if (response?.data?.success) {
        setWorkSchedules(response?.data?.data);

        // console.log("レスポンス", response?.data?.data);

        const existingIds = new Set();
        const responseEmployees = (!response?.data?.data || !Array.isArray(response.data.data)) ? [] : response?.data.data.map((item) => ({
          label: `${item?.employee?.last_name} ${item?.employee?.first_name}`,
          value: item?.employee?.id,
          type: "employee",
        }))
        .filter((item) => { // filter for unique ids
          return !existingIds.has(item.value) && existingIds.add(item.value);
        });

        const currentUserId = auth?.userInfo?.id;
        const currentUserLabel = auth?.userInfo ? `${auth.userInfo.last_name} ${auth.userInfo.first_name}` : "";
        const isUserInList = responseEmployees.some(opt => Number(opt.value) === Number(currentUserId));

        if (currentUserId && !isUserInList) {
          // Put the current user at the front
          responseEmployees.unshift({
            label: currentUserLabel || "自分",
            value: currentUserId,
            type: "employee",
          });
        }

        //ユーザー選択履歴との照らし合わせのため一旦optionEmployeeRawに保存
        setEmployeeOptionsRaw(responseEmployees);
        setLoading(false);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // URL -> 初期値復元（直リンク・戻る/進む対応：monthの追従）
  React.useEffect(() => {
    const monthParam = getQueryParam("month");
    if (hasQueryValue(monthParam)) {
      const parsed = moment(`${monthParam}-01`, "YYYY-MM-DD", true);
      if (parsed.isValid()) {
        const currentMonth = moment(date).format("YYYY-MM");
        if (currentMonth !== monthParam) {
          setDate(parsed.toDate());
        }
      }
    } else {
      // クエリにmonthが無い場合は今月を表示（URLは書き換えない）
      const nowMonth = moment().format("YYYY-MM");
      const currentMonth = moment(date).format("YYYY-MM");
      if (currentMonth !== nowMonth) {
        setDate(moment(`${nowMonth}-01`, "YYYY-MM-DD", true).toDate());
      }
    }
  }, [currentQueryString]);

  // people/contractor は候補が出揃ってから復元（戻る/進む対応。常にURLを優先）
  // 備考:
  // - userId=all / groupId=all は「未選択」を意味するセンチネル値（URLだけで状態を完全表現するため）
  // - contractorId はセンチネル不要（未選択＝欠如）
  React.useEffect(() => {
    const userIdParam = getQueryParam("userId") || getQueryParam("user_id");
    const groupIdParam = getQueryParam("groupId") || getQueryParam("group_id");
    const contractorIdParam = getQueryParam("contractorId");

    // contractor（請負業者）はURLに値があるときは反映、無いときはクリア
    if (hasQueryValue(contractorIdParam)) setSelectSubContractor(contractorIdParam as string);
    else if (selectSubContractor) setSelectSubContractor("");

    if (!peopleOptions || peopleOptions.length === 0) return;

    const hasUserParam = hasQueryValue(userIdParam);
    const hasGroupParam = hasQueryValue(groupIdParam);
    const userIsAll = userIdParam === "all";
    const groupIsAll = groupIdParam === "all";

    if (hasUserParam && !userIsAll) {
      const opt = peopleOptions.find(o => o.type === "employee" && Number(o.value) === Number(userIdParam));
      if (opt && (peopleInput?.type !== "employee" || Number(peopleInput?.value) !== Number(userIdParam))) {
        setPeopleInput(opt);
      }
      return;
    }
    if (hasGroupParam && !groupIsAll) {
      const opt = peopleOptions.find(o => o.type === "group" && Number(o.value) === Number(groupIdParam));
      if (opt && (peopleInput?.type !== "group" || Number(peopleInput?.value) !== Number(groupIdParam))) {
        setPeopleInput(opt);
      }
      return;
    }

    // all が指定されている場合はいずれにせよクリア
    if ((hasUserParam && userIsAll) || (hasGroupParam && groupIsAll)) {
      if (peopleInput) setPeopleInput(null);
      return;
    }

    // 両方指定なし → ログイン者をデフォルト選択（pushはしない）
    if (!hasUserParam && !hasGroupParam) {
      const currentUserId = auth?.userInfo?.id;
      const meOption = peopleOptions.find(o => o.type === "employee" && Number(o.value) === Number(currentUserId));
      if (meOption) {
        if (peopleInput?.type !== "employee" || Number(peopleInput?.value) !== Number(currentUserId)) {
          setPeopleInput(meOption);
        }
      }
      return;
    }
  }, [peopleOptions, currentQueryString]);

  // URLクエリへ現在のフィルターを反映する共通関数
  // 備考:
  // - userId / groupId は「未選択」を 'all' で明示する仕様（戻る/進む・直リンク時の確実な復元のため）
  //   → 例: userId=all を見たら「従業員は未選択」と判断できる
  // - 請負業者(contractorId) は既定値を持たないため、未選択はクエリ欠如で十分（'all' は不要）
  const pushFilters = (overrides: Partial<Record<string, string | number | undefined>> = {}) => {
    const monthParam = moment(date).format("YYYY-MM");
    const base: Record<string, any> = {
      selectedView: "workSchedule",
      month: monthParam,
      contractorId: selectSubContractor || undefined,
    };
    if (peopleInput?.type === "employee") base.userId = peopleInput.value;
    if (peopleInput?.type === "group") base.groupId = peopleInput.value;

    const merged = { ...base, ...overrides };
    navigateWithState(currentPath, merged, "push");
  };

  //ユーザー選択されるたびに履歴順に並び替え
  React.useEffect(()=>{
    if (!Array.isArray(list) || !Array.isArray(employeeOptionsRaw)) return;
    if (!list.length || !employeeOptionsRaw.length) return;

    const sortedIds = list.map(user => user.id);
    const currentUserId = auth?.userInfo?.id;

    const sortedEmployees = [...employeeOptionsRaw].sort(
        (a, b) => {
          // Current user always first
          if (currentUserId) {
            const aIsMe = Number(a.value) === Number(currentUserId);
            const bIsMe = Number(b.value) === Number(currentUserId);
            if (aIsMe && !bIsMe) return -1;
            if (!aIsMe && bIsMe) return 1;
          }

          // then sort by selection history
          const indexA = sortedIds.indexOf(Number(a.value)); 
          const indexB = sortedIds.indexOf(Number(b.value));
          if (indexA === -1 && indexB === -1) return 0;
          if (indexA === -1) return 1;
          if (indexB === -1) return -1;
          return indexA - indexB;
        }
      );

    // console.log("並び替え後", sortedEmployees);
    setEmployeeOptions(sortedEmployees);

  },[list, employeeOptionsRaw]);

  // 既定選択は上のURL復元useEffectで一元管理（初回のみ/URL無し時のみ）

  const handleChangeMonth = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const newDate = moment(value, "YYYY-MM-DD", true).toDate();
    setDate(newDate);
    setSelectSubContractor("");
    const monthParam = moment(newDate).format("YYYY-MM");
    pushFilters({ month: monthParam, contractorId: undefined });
  };

  const handleOnChangePeople = (value: DropdownOption | null) => {
    const prev = peopleInput;
    setPeopleInput(value);
    if (!value) {
      // クリア時は直前の選択種別に応じて all を push（URLから確実に「未選択」を復元できるようにするため）
      if (prev?.type === "employee") {
        pushFilters({ userId: "all" });
      } else if (prev?.type === "group") {
        pushFilters({ groupId: "all" });
      } else {
        pushFilters();
      }
      return;
    }

    if (value.type === "employee") {
      storeSelection({ user_id: id, selected_user_id: value.value });
    }
    if (value.type === "group") {
      storeGroupSelection({ user_id: id, selected_group_id: value.value });
    }
    if (value.type === "employee") {
      pushFilters({ userId: value.value, groupId: undefined });
    } else if (value.type === "group") {
      pushFilters({ groupId: value.value, userId: undefined });
    } else {
      pushFilters();
    }
  };

  const handleOnChangeSubContracter = (option) => {
    const id = option ? option.value : undefined;
    setSelectSubContractor(id || "");
    pushFilters({ contractorId: id || undefined });
  };

  const filteredWorkSchedules = filterByPeople();

  return (
    <div>
      <CircularLoader loading={loading} />

      <div className="workSchedule__form mb-5">
        <div className="container">
          <div className="workSchedule__datePicker">
            <input
              type="date"
              value={`${moment(date).format("YYYY-MM-01")}`}
              onChange={handleChangeMonth}
              className="inputMonth"
            />
          </div>

          <div className="subContainer">
            <div className="workSchedule__select">
              <div className="wrapperSelect">
                {!!contractors?.length && (
                  <AppCustomeSelect
                    placeholder={PLACEHOLDER_CONTRACTOR}
                    onChange={handleOnChangeSubContracter}
                    groupUserOptions={contractorOptions}
                    options={contractorOptions}
                    isClearable
                    value={contractorOptions.find(o => String(o.value) === String(selectSubContractor)) || null}
                  />
                )}
              </div>
            </div>
            <div className="workSchedule__emp">
              <label className="label">班・従業員</label>
              <div className="workSchedule__empSelect">
                <AppCustomeSelect
                  placeholder="人を選択する"
                  onChange={handleOnChangePeople}
                  options={peopleOptions}
                  isClearable
                  value={peopleInput}
                />
              </div>
            </div>

          </div>
        </div>
      </div>
      <div className="workSchedule__content">
        <TableWrapView data={filteredWorkSchedules} date={date} />
      </div>
    </div>
  );
};

/*
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={9} xl={8}>
            <div className="workSchedule__filter">
              <div className="workSchedule__button">
                <StyledGrayToggleButton isActive={!selectedGroup.length}
                  onClick={() => handleSelectGroup(defaultGroup.id)}
                >
                  全て</StyledGrayToggleButton>
              </div>

              {groups &&
                groups.map((item) => (
                  <div className="workSchedule__button">
                    <StyledGrayToggleButton key={item.id}
                      isActive={checkExistUserSelected(item.id, selectedGroup)}
                      onClick={() => {
                        handleSelectGroup(item.id);
                      }}
                    >
                      {item.name}</StyledGrayToggleButton>
                  </div>

                ))}
            </div>
          </Grid>
        </Grid>
*/